import React, { memo } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { transitions } from '@styles/animations';
import { formatTimestamp } from '@utils/chat';

import { Avatar } from '../Avatar';
import { StatusIndicator } from '../StatusIndicator';

import type { MessageBubbleProps } from './MessageBubble.types';

/**
 * Complete message bubble with avatar, content, timestamp, and streaming indicator
 * Automatically handles layout for user and assistant messages
 *
 * Memoized to prevent unnecessary re-renders when props haven't changed.
 *
 * @example
 * ```tsx
 * <MessageBubble
 *   id="msg-1"
 *   role="assistant"
 *   content="Hello! How can I help you?"
 *   timestamp="2024-01-15T14:30:00Z"
 * />
 *
 * <MessageBubble
 *   id="msg-2"
 *   role="user"
 *   content="I need help with my document"
 *   timestamp="2024-01-15T14:31:00Z"
 *   isHovered={isHovered('msg-2')}
 *   onMouseEnter={handleMouseEnter}
 *   onMouseLeave={handleMouseLeave}
 * />
 * ```
 */
export const MessageBubble = memo<MessageBubbleProps>(({
  id,
  role,
  content,
  timestamp,
  isStreaming = false,
  avatarContent,
  maxBubbleWidth = '75%',
  isHovered = false,
  onMouseEnter,
  onMouseLeave,
  className,
}) => {
  const theme = useTheme();
  const isUser = role === 'user';

  const handleMouseEnter = () => {
    onMouseEnter?.(id);
  };

  const handleMouseLeave = () => {
    onMouseLeave?.();
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        position: 'relative',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Avatar for assistant */}
      {!isUser && <Avatar role="assistant" icon={avatarContent} />}

      {/* Message bubble */}
      <Paper
        elevation={isUser ? 2 : 1}
        sx={{
          p: 1.5,
          maxWidth: maxBubbleWidth,
          bgcolor: isUser
            ? theme.palette.chat.colors.userBubble
            : theme.palette.chat.colors.messageBubbleBackground,
          color: isUser ? theme.palette.chat.colors.userText : theme.palette.chat.colors.assistantText,
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : 16,
          borderTopLeftRadius: isUser ? 16 : 0,
          boxShadow: isUser ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
          border: isUser ? 'none' : `1px solid ${theme.palette.chat.colors.messageBubbleBorder}`,
          transition: transitions.messageHover,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: isUser ? '0 4px 8px rgba(0,0,0,0.15)' : '0 2px 6px rgba(0,0,0,0.1)',
          },
        }}
      >
        {/* Header with role label and status indicator */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                color: isUser
                  ? 'rgba(255,255,255,0.7)'
                  : theme.palette.chat.colors.timestampText,
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              {isUser ? 'You' : 'Assistant'}
            </Typography>
            {isStreaming && <StatusIndicator status="streaming" size="sm" />}
          </Box>
          {isHovered && timestamp && (
            <Typography
              variant="caption"
              sx={{
                color: isUser
                  ? 'rgba(255,255,255,0.7)'
                  : theme.palette.chat.colors.timestampTextLight,
                fontSize: 11,
                animation: 'fadeIn 0.2s ease-in',
              }}
            >
              {formatTimestamp(timestamp)}
            </Typography>
          )}
        </Box>

        {/* Message content */}
        {content && (
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}
          >
            {content}
          </Typography>
        )}
      </Paper>

      {/* Avatar for user */}
      {isUser && <Avatar role="user" icon={avatarContent} />}
    </Box>
  );
});

MessageBubble.displayName = 'MessageBubble';
